import React, { createContext, useState, useContext, useEffect } from 'react';
import { useLocation } from 'wouter';

type TipId = string;

interface ObiTip {
  id: TipId;
  title?: string;
  content: string;
  variant?: 'info' | 'success' | 'warning';
  actionText?: string;
  actionLink?: string;
  showChatButton?: boolean;
  pages?: string[]; // Array of paths where this tip should be shown
  condition?: () => boolean; // Function to determine if the tip should be shown
}

interface ObiTipsContextType {
  tips: ObiTip[];
  dismissedTips: TipId[];
  addTip: (tip: ObiTip) => void;
  removeTip: (id: TipId) => void;
  dismissTip: (id: TipId) => void;
  resetDismissed: () => void;
}

const ObiTipsContext = createContext<ObiTipsContextType | null>(null);

// Default tips for different pages
const defaultTips: ObiTip[] = [
  {
    id: 'dashboard-welcome',
    title: 'Welcome to your dashboard',
    content: "Complete the tasks in order to get started with your legal journey. I'm Obi, your AI assistant.",
    variant: 'info',
    pages: ['/'],
  },
  {
    id: 'id-verification-guide',
    title: 'ID Verification',
    content: 'Please make sure all information matches your ID document exactly to avoid verification issues.',
    variant: 'warning',
    pages: ['/id-verification'],
  },
  {
    id: 'document-upload-guide',
    title: 'Uploading Documents',
    content: 'For best results, use well-lit, clear images of your documents with all corners visible.',
    variant: 'info',
    pages: ['/id-verification/upload', '/documents'],
  },
  {
    id: 'settings-security',
    title: 'Security Tip',
    content: 'We recommend enabling two-factor authentication for added security of your account.',
    variant: 'warning',
    actionText: 'Enable 2FA',
    actionLink: '/settings',
    pages: ['/settings'],
  },
  {
    id: 'payment-reminder',
    title: 'Payment Information',
    content: 'All payments are securely processed through our payment provider with end-to-end encryption.',
    variant: 'info',
    pages: ['/payments'],
  },
  {
    id: 'documents-tip',
    title: 'Document Management',
    content: 'You can view all your uploaded documents here. Make sure to keep them updated and review them regularly.',
    variant: 'info',
    pages: ['/documents'],
  },
  {
    id: 'cases-tip',
    title: 'Case Management',
    content: 'Keep track of your ongoing cases and their progress here. You can click on any case for more details.',
    variant: 'info',
    pages: ['/cases'],
  },
  {
    id: 'appointments-tip',
    title: 'Appointment Scheduling',
    content: "Schedule and manage your appointments with your legal team. You'll receive reminders before each appointment.",
    variant: 'info',
    pages: ['/appointments'],
  },
  {
    id: 'messages-tip',
    title: 'Secure Messaging',
    content: "All communications with your legal team are private and secure. You'll be notified of new messages.",
    variant: 'info',
    pages: ['/messages'],
  },
  {
    id: 'ai-chat-tip',
    title: 'Chat with Obi',
    content: "I'm here to help you with any questions you have about your legal matters or using this platform.",
    variant: 'success',
    pages: ['/ai-chat'],
  }
];

export const ObiTipsProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [tips, setTips] = useState<ObiTip[]>(defaultTips);
  const [dismissedTips, setDismissedTips] = useState<TipId[]>([]);
  const [location] = useLocation();
  
  // Load dismissed tips from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('dismissedObiTips');
    if (saved) {
      try {
        setDismissedTips(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading dismissed tips:', e);
      }
    }
  }, []);
  
  // Save dismissed tips to localStorage when they change
  useEffect(() => {
    localStorage.setItem('dismissedObiTips', JSON.stringify(dismissedTips));
  }, [dismissedTips]);
  
  const addTip = (tip: ObiTip) => {
    setTips(prev => {
      // Replace if tip with same ID exists, otherwise add
      const exists = prev.some(t => t.id === tip.id);
      if (exists) {
        return prev.map(t => t.id === tip.id ? tip : t);
      } else {
        return [...prev, tip];
      }
    });
  };
  
  const removeTip = (id: TipId) => {
    setTips(prev => prev.filter(tip => tip.id !== id));
  };
  
  const dismissTip = (id: TipId) => {
    setDismissedTips(prev => [...prev, id]);
  };
  
  const resetDismissed = () => {
    setDismissedTips([]);
    localStorage.removeItem('dismissedObiTips');
  };
  
  return (
    <ObiTipsContext.Provider
      value={{
        tips,
        dismissedTips,
        addTip,
        removeTip,
        dismissTip,
        resetDismissed
      }}
    >
      {children}
    </ObiTipsContext.Provider>
  );
};

export const useObiTips = () => {
  const context = useContext(ObiTipsContext);
  if (!context) {
    throw new Error('useObiTips must be used within an ObiTipsProvider');
  }
  
  const [location] = useLocation();
  
  // Filter tips based on current page and dismissed status
  const visibleTips = context.tips.filter(tip => {
    // Skip if tip has been dismissed
    if (context.dismissedTips.includes(tip.id)) return false;
    
    // Show on all pages if no pages specified
    if (!tip.pages || tip.pages.length === 0) return true;
    
    // Check if current page matches any in the pages array
    const pageMatch = tip.pages.some(page => {
      // Exact match
      if (page === location) return true;
      
      // Match with wildcard (e.g., '/documents/*')
      if (page.endsWith('/*') && location.startsWith(page.slice(0, -2))) return true;
      
      return false;
    });
    
    if (!pageMatch) return false;
    
    // Check custom condition if provided
    if (tip.condition && !tip.condition()) return false;
    
    return true;
  });
  
  return {
    ...context,
    visibleTips
  };
};